import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Button,
  TextField,
  DialogActions,
  Typography,
  Autocomplete,
  Tab,
  DialogTitle,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  createEmissionRecord,
  updateEmissionRecord,
  deleteEmissionRecord,
} from "reduxStore/actions/emissionRecordActions";
import { useDispatch, useSelector } from "react-redux";
import { checkRoles } from "utils/permissions";
import { fetchOrganisations } from "reduxStore/actions/organisationActions";

const tabConfig = [
  { label: "Raw Material", value: "raw_material" },
  { label: "Emissions", value: "emissions" },
  { label: "Ingredient Attributes", value: "ingredientAttributes" },
];
// at the top of EmissionRecordForm.js
const REGION_OPTIONS = [
  "Africa",
  "Argentina",
  "Asia",
  "Australia and New Zealand",
  "Australia",
  "Austria",
  "Belgium",
  "Brazil",
  "British Isles",
  "Bulgaria",
  "Canada",
  "China",
  "Czechia",
  "Denmark",
  "East Asia and Southeast Asia",
  "Eastern Europe",
  "Estonia",
  "EU",
  "Europe",
  "Finland",
  "France",
  "Germany",
  "global",
  "Greece",
  "Hungary",
  "India",
  "Indonesia",
  "Ireland",
  "Italy",
  "Ivory Coast",
  "Latin America and the Caribbean",
  "Latvia",
  "Lithuania",
  "Malaysia",
  "Mexico",
  "Morocco",
  "Multi-region",
  "Near East and North Africa",
  "Netherlands",
  "North America",
  "Norway",
  "Oceania",
  "Pakistan",
  "Poland",
  "Portugal",
  "Region of Europe",
  "Reunion Island",
  "Romania",
  "Russian Federation",
  "South America",
  "South Asia",
  "Spain",
  "Sub-Saharan Africa",
  "Sudan",
  "Sweden",
  "Switzerland",
  "Thailand",
  "UK import market mix",
  "UK",
  "Ukraine",
  "US",
  "Wales",
  "West Indies",
  "Western Europe",
  "World average",
];

export default function EmissionRecordForm({
  editMode = false,
  setOpen,
  currentIngredient,
  handleClose,
  open,
}) {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user || {});
  const organsiationId = user?.organisation?._id;
  const { isSystemAdmin } = checkRoles(user);

  const organisations = useSelector((state) => state.organisations.data);

  const SYSTEM_WIDE_OPTION = { _id: "", name: "System-wide" };

  const organisationOptions = isSystemAdmin
    ? [SYSTEM_WIDE_OPTION, ...organisations]
    : organisations;

  const [currentTab, setCurrentTab] = useState("raw_material");
  const FUNCTIONAL_UNIT_OPTIONS = ["1 kg", "1 litre"];
  const SOURCE_DB_NAME_OPTIONS = [
    "Internal lifecycle assessment",
    "Other",
    "Unknown",
  ];

  const initialValues = {
    organisation:
      currentIngredient?.organisation?._id ??
      (isSystemAdmin ? "" : organsiationId),
    gpc_classification: currentIngredient?.gpc_classification || "",
    source_product_name: currentIngredient?.source_product_name || "",
    source_db_name: currentIngredient?.source_db_name || "",
    data_quality_score: currentIngredient?.data_quality_score || "",
    origin_region: currentIngredient?.origin_region || "",
    applicable_region: currentIngredient?.applicable_region || "",
    production_system: currentIngredient?.production_system || "",
    functional_unit: currentIngredient?.functional_unit || "",
    emissions: {
      cradle_to_processing_gate_plus_transport:
        currentIngredient?.emissions
          ?.cradle_to_processing_gate_plus_transport || "",
      cradle_to_farm_gate:
        currentIngredient?.emissions?.cradle_to_farm_gate || "",
      cradle_to_plant_gate_feed_only:
        currentIngredient?.emissions?.cradle_to_plant_gate_feed_only || "",
      land_use_change: currentIngredient?.emissions?.land_use_change || "",
      feed: currentIngredient?.emissions?.feed || "",
      farm: currentIngredient?.emissions?.farm || "",
      processing: currentIngredient?.emissions?.processing || "",
      packaging: currentIngredient?.emissions?.packaging || "",
      transport: currentIngredient?.emissions?.transport || "",
    },
  };

  const validationSchema = Yup.object().shape({
    organisation: Yup.string(),
    gpc_classification: Yup.string().required("Required"),
    source_product_name: Yup.string().required("Required"),
    source_db_name: Yup.string()
      .trim()
      .max(120, "Keep it under 120 characters"),
    data_quality_score: Yup.number().min(0).max(5),
    origin_region: Yup.string(),
    applicable_region: Yup.string(),
    production_system: Yup.string(),
    functional_unit: Yup.string()
      .trim()
      .max(120, "Keep it under 120 characters"),
    emissions: Yup.object().shape({
      cradle_to_processing_gate_plus_transport: Yup.number().min(0),
      cradle_to_farm_gate: Yup.number().min(0),
      cradle_to_plant_gate_feed_only: Yup.number().min(0),
      land_use_change: Yup.number().min(0),
      feed: Yup.number().min(0),
      farm: Yup.number().min(0),
      processing: Yup.number().min(0),
      packaging: Yup.number().min(0),
      transport: Yup.number().min(0),
    }),
  });

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      source_db_name: (values.source_db_name || "").trim(),
    };

    if (!isSystemAdmin) {
      payload.organisation = organsiationId;
    }

    if (editMode) {
      await dispatch(
        updateEmissionRecord({ id: currentIngredient._id, values: payload })
      );
    } else {
      await dispatch(createEmissionRecord(payload));
    }

    setOpen(false);
  };

  const handleDelete = () => {
    if (currentIngredient?._id)
      dispatch(deleteEmissionRecord(currentIngredient._id));
    setOpen(false);
  };

  const capitaliseFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  useEffect(() => {
    dispatch(fetchOrganisations());
  }, [dispatch]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editMode ? "Edit Emission Record" : "Add New Emission Record"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, errors, touched, setFieldValue }) => (
          <Form>
            <TabContext value={currentTab}>
              <Box
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2, mx: 3 }}
              >
                <TabList onChange={(_, val) => setCurrentTab(val)}>
                  {tabConfig.map(({ label, value }) => (
                    <Tab key={value} label={label} value={value} />
                  ))}
                </TabList>
              </Box>

              {/* RAW MATERIAL TAB */}
              <TabPanel value="raw_material">
                <Typography variant="h6" gutterBottom>
                  {editMode
                    ? "Edit Emission Record"
                    : "Add New Emission Record"}
                </Typography>
                <TextField
                  fullWidth
                  margin="dense"
                  label="Raw Material Name"
                  name="source_product_name"
                  value={values.source_product_name}
                  onChange={handleChange}
                  error={
                    touched.source_product_name &&
                    Boolean(errors.source_product_name)
                  }
                  helperText={
                    touched.source_product_name && errors.source_product_name
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  label="Emissions Category"
                  name="gpc_classification"
                  value={values.gpc_classification}
                  onChange={handleChange}
                  error={
                    touched.gpc_classification &&
                    Boolean(errors.gpc_classification)
                  }
                  helperText={
                    touched.gpc_classification && errors.gpc_classification
                  }
                />

                <Autocomplete
                  freeSolo
                  options={SOURCE_DB_NAME_OPTIONS}
                  value={values.source_db_name || ""}
                  // Update when the user selects an option or confirms a custom value
                  onChange={(_, newValue) => {
                    // newValue will be a string for both options and free text
                    setFieldValue("source_db_name", newValue || "");
                  }}
                  // Update as they type (so Formik value reflects the text box immediately)
                  onInputChange={(_, newInputValue, reason) => {
                    // Only sync user input changes (ignore resets from option selection)
                    if (reason === "input")
                      setFieldValue("source_db_name", newInputValue || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Emissions Data Source"
                      margin="dense"
                      name="source_db_name"
                      fullWidth
                      error={
                        touched.source_db_name && Boolean(errors.source_db_name)
                      }
                      helperText={
                        (touched.source_db_name && errors.source_db_name) ||
                        "Choose an option or type your own"
                      }
                    />
                  )}
                />

                {isSystemAdmin && (
                  <>
                    <Autocomplete
                      options={organisationOptions}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? option
                          : option.name || "Unnamed Organisation"
                      }
                      value={
                        organisationOptions.find(
                          (option) =>
                            option._id === values.organisation ||
                            (option._id === "" && values.organisation === "")
                        ) ?? SYSTEM_WIDE_OPTION
                      }
                      onChange={(event, newValue) => {
                        // Set empty string for System-wide
                        setFieldValue("organisation", newValue?._id || "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Organisation"
                          variant="outlined"
                          margin="dense"
                          error={
                            touched.organisation && Boolean(errors.organisation)
                          }
                          helperText={
                            touched.organisation && errors.organisation
                          }
                        />
                      )}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Selecting “System-wide” makes this emission record
                      accessible to all users.
                    </Typography>
                  </>
                )}
              </TabPanel>

              {/* EMISSIONS TAB */}
              <TabPanel value="emissions">
                <Typography variant="h6" gutterBottom>
                  Emissions Breakdown
                </Typography>
                {Object.entries(values.emissions).map(([key]) => (
                  <TextField
                    key={key}
                    fullWidth
                    margin="dense"
                    type="number"
                    label={
                      key === "cradle_to_processing_gate_plus_transport"
                        ? "Total Emissions Factor (kg CO₂e)"
                        : capitaliseFirstLetter(key.replace(/_/g, " ")) +
                          " (kg CO₂e)"
                    }
                    name={`emissions.${key}`}
                    value={values.emissions[key]}
                    onChange={handleChange}
                    error={
                      touched.emissions?.[key] &&
                      Boolean(errors.emissions?.[key])
                    }
                    helperText={
                      touched.emissions?.[key] && errors.emissions?.[key]
                    }
                  />
                ))}
              </TabPanel>

              {/* Ingredient Attributes TAB */}
              <TabPanel value="ingredientAttributes">
                <Typography variant="h6" gutterBottom>
                  Ingredient Attributes
                </Typography>
                {isSystemAdmin && (
                  <TextField
                    fullWidth
                    margin="dense"
                    type="number"
                    label="Data Quality Score"
                    name="data_quality_score"
                    value={values.data_quality_score}
                    onChange={handleChange}
                    error={
                      touched.data_quality_score &&
                      Boolean(errors.data_quality_score)
                    }
                    helperText={
                      touched.data_quality_score && errors.data_quality_score
                    }
                  />
                )}
                <Autocomplete
                  options={REGION_OPTIONS}
                  value={values.origin_region}
                  onChange={(_, newVal) =>
                    setFieldValue("origin_region", newVal || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Origin Region"
                      margin="dense"
                      name="origin_region"
                      fullWidth
                      error={
                        touched.origin_region && Boolean(errors.origin_region)
                      }
                      helperText={touched.origin_region && errors.origin_region}
                    />
                  )}
                />

                <Autocomplete
                  options={REGION_OPTIONS}
                  value={values.applicable_region}
                  onChange={(_, newVal) =>
                    setFieldValue("applicable_region", newVal || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Applicable Region"
                      margin="dense"
                      name="applicable_region"
                      fullWidth
                      error={
                        touched.applicable_region &&
                        Boolean(errors.applicable_region)
                      }
                      helperText={
                        touched.applicable_region && errors.applicable_region
                      }
                    />
                  )}
                />
                {/* <TextField
                  fullWidth
                  margin="dense"
                  label="Production System"
                  name="production_system"
                  value={values.production_system}
                  onChange={handleChange}
                  error={
                    touched.production_system &&
                    Boolean(errors.production_system)
                  }
                  helperText={
                    touched.production_system && errors.production_system
                  }
                /> */}
                <Autocomplete
                  freeSolo
                  options={FUNCTIONAL_UNIT_OPTIONS}
                  value={values.functional_unit || ""}
                  // Handle option select or free text
                  onChange={(_, newValue) => {
                    setFieldValue("functional_unit", newValue || "");
                  }}
                  // Keep Formik in sync as user types
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      setFieldValue("functional_unit", newInputValue || "");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Functional Unit"
                      margin="dense"
                      name="functional_unit"
                      fullWidth
                      error={
                        touched.functional_unit &&
                        Boolean(errors.functional_unit)
                      }
                      helperText={
                        (touched.functional_unit && errors.functional_unit) ||
                        "Choose 1 kg, 1 litre, or type your own unit"
                      }
                    />
                  )}
                />
              </TabPanel>

              <DialogActions sx={{ mt: 2 }}>
                {editMode &&
                  (isSystemAdmin || // Admins can always delete
                    currentIngredient.organisation?._id === organsiationId) && (
                    <Button color="error" onClick={handleDelete}>
                      Delete
                    </Button>
                  )}

                <Button onClick={() => setOpen(false)} color="inherit">
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  {editMode ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </TabContext>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
